/**
 * @file Which input scheme this device gets.
 *
 * One decision, made once at boot and carried on the context: `'touch'` gives
 * the player on-screen sticks and buttons, `'pointer'` is the keyboard/mouse
 * game everything else has always played.
 *
 * The test is a capability question, never a size question. A narrow desktop
 * window is not a phone and must keep its mouse; a 1024px tablet held in
 * landscape is a phone as far as controls are concerned. `(pointer: coarse)`
 * asks about the *primary* pointer, which is exactly that distinction, and the
 * touch-point count rules out the odd desktop that reports coarse for a stylus
 * or a TV remote.
 */

/** Input schemes the game can present. @readonly @enum {string} */
export const InputMode = Object.freeze({
  POINTER: 'pointer',
  TOUCH: 'touch'
});

/**
 * Whether this device should be driven by touch.
 *
 * Every browser API here is optional-chained: the module is imported by plain
 * (non-jsdom) vitest runs, where `window` and `navigator` may not exist at all.
 *
 * @param {{ window?: any, navigator?: any }} [env] Injected globals, for tests.
 * @returns {boolean}
 */
export function detectTouchDevice(env = {}) {
  const view = env.window ?? (typeof window !== 'undefined' ? window : null);
  const nav = env.navigator ?? (typeof navigator !== 'undefined' ? navigator : null);
  if (!view || !nav) return false;

  const coarse = view.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  const touchPoints = nav.maxTouchPoints ?? 0;
  return coarse && touchPoints > 0;
}

/**
 * Resolves the scheme to use, letting a developer force either one.
 *
 * Order is deliberate: an explicit `?input=` in the URL is the loudest signal a
 * tester can send and beats everything, then the developer window's switch,
 * then what the hardware says.
 *
 * @param {object} [options]
 * @param {string} [options.search] `location.search`; read from `window` when omitted.
 * @param {string | null} [options.override] Forced mode from the developer window.
 * @param {{ window?: any, navigator?: any }} [options.env] Injected globals, for tests.
 * @returns {string} One of {@link InputMode}.
 */
export function resolveInputMode({ search, override = null, env = {} } = {}) {
  const view = env.window ?? (typeof window !== 'undefined' ? window : null);
  const query = search ?? view?.location?.search ?? '';

  const requested = new URLSearchParams(query).get('input');
  if (requested === InputMode.TOUCH || requested === InputMode.POINTER) return requested;

  if (override === InputMode.TOUCH || override === InputMode.POINTER) return override;

  return detectTouchDevice(env) ? InputMode.TOUCH : InputMode.POINTER;
}
